import {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useTranslation} from 'react-i18next'

import {HistoryIcon, SearchIcon} from 'components'
import {KEY_CODES} from 'definitions/constant'
import {DopamineSessionSummary} from 'interfaces'
import {DopamineAssistantSessionController} from './DopamineAssistant.types'
import {
  doesDopamineSessionMatchSearch,
  formatDopamineSessionCreatedAt,
} from './DopamineAssistant.utils'

type DopamineAssistantHistoryProps = {
  isOpen: boolean
  onClose(): void
  onOpen(): void
  session: DopamineAssistantSessionController
}

type DopamineAssistantHistoryListProps = {
  onSelect(item: DopamineSessionSummary): void
  search: string
  session: DopamineAssistantSessionController
  visibleSessions: DopamineSessionSummary[]
}

const DopamineAssistantHistoryList = ({
  onSelect,
  search,
  session,
  visibleSessions,
}: DopamineAssistantHistoryListProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const showEmptyState =
    !session.isSessionsLoading &&
    !session.isSessionsError &&
    visibleSessions.length === 0

  return (
    <div
      aria-busy={session.isSessionsLoading}
      className="dopamine-history-body"
    >
      {session.isSessionsLoading ? (
        <p className="px-2 py-4 text-center text-xs text-gray-500">
          {t('DOPAMINE_LOADING_PAST_CHATS')}
        </p>
      ) : null}
      {session.isSessionsError ? (
        <p className="px-2 py-4 text-center text-xs text-red-400" role="alert">
          {t('DOPAMINE_PAST_CHATS_ERROR')}
        </p>
      ) : null}
      {showEmptyState ? (
        <p className="px-2 py-4 text-center text-xs text-gray-500">
          {search.trim()
            ? t('DOPAMINE_NO_MATCHING_CHATS')
            : t('DOPAMINE_NO_PAST_CHATS')}
        </p>
      ) : null}
      {visibleSessions.map(item => (
        <button
          aria-label={item.title}
          className="dopamine-history-row"
          data-cy={`dopamine-assistant-session-${item.sessionId}`}
          key={item.sessionId}
          type="button"
          onClick={() => onSelect(item)}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-xs text-white/90">
              {item.title}
            </span>
            <span className="mt-0.5 block truncate text-10px text-gray-600">
              {formatDopamineSessionCreatedAt(item.createdAt)}
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}

export const DopamineAssistantHistory = ({
  isOpen,
  onClose,
  onOpen,
  session,
}: DopamineAssistantHistoryProps): ReactElement => {
  const {t} = useTranslation(['common'], {useSuspense: false})
  const [search, setSearch] = useState('')
  const dialogRef = useRef<HTMLDialogElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const visibleSessions = useMemo(() => {
    const query = search.trim()
    return query
      ? session.sessions.filter(item =>
          doesDopamineSessionMatchSearch(item, query),
        )
      : session.sessions
  }, [search, session.sessions])
  const selectSession = (item: DopamineSessionSummary): void => {
    session.selectSession(item)
    onClose()
    triggerRef.current?.focus()
  }

  const handleSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)
  }

  const handleDialogKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== KEY_CODES.ESCAPE) return

    event.preventDefault()
    event.stopPropagation()
    onClose()
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!isOpen) setSearch('')
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleOutsidePointerDown = (event: PointerEvent): void => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (
        dialogRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return
      }

      onClose()
      window.setTimeout(() => {
        const activeElement = document.activeElement
        if (
          activeElement === null ||
          activeElement === document.body ||
          !activeElement.isConnected
        ) {
          triggerRef.current?.focus()
        }
      }, 0)
    }

    document.addEventListener('pointerdown', handleOutsidePointerDown)
    return () =>
      document.removeEventListener('pointerdown', handleOutsidePointerDown)
  }, [isOpen, onClose])

  return (
    <>
      <button
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={t('DOPAMINE_PAST_CHATS')}
        className="dopamine-header-icon-button"
        data-cy="dopamine-assistant-past-chats"
        data-tip={t('DOPAMINE_PAST_CHATS')}
        ref={triggerRef}
        type="button"
        onClick={isOpen ? onClose : onOpen}
      >
        <HistoryIcon height={15} strokeWidth={1.7} width={15} />
      </button>
      {isOpen ? (
        <dialog
          aria-label={t('DOPAMINE_PAST_CHATS')}
          className="dopamine-history-popover"
          ref={dialogRef}
          open
          onKeyDown={handleDialogKeyDown}
        >
          <header className="dopamine-history-header">
            <p className="dopamine-history-caption">
              {t('DOPAMINE_PAST_CHATS')}
            </p>
            <label className="dopamine-history-search">
              <SearchIcon
                classes=""
                height={14}
                strokeColor="currentColor"
                strokeWidth={1.8}
                width={14}
              />
              <input
                aria-label={t('DOPAMINE_SEARCH_PAST_CHATS')}
                autoFocus
                className="min-w-0 flex-1 bg-transparent text-13px text-white outline-none placeholder:text-gray-600"
                placeholder={t('DOPAMINE_SEARCH_PAST_CHATS')}
                type="search"
                value={search}
                onChange={handleSearch}
              />
            </label>
          </header>
          <DopamineAssistantHistoryList
            search={search}
            session={session}
            visibleSessions={visibleSessions}
            onSelect={selectSession}
          />
        </dialog>
      ) : null}
    </>
  )
}
